import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import {
  ResourceContainer,
  type ResourceSection,
} from "@/components/custom/resources/resource-container";
import { ResourceViewSwitcher } from "@/components/custom/resources/resource-view-switcher";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useSuspenseGetMcpGateways } from "./api/hooks";
import { GatewaysEmptyState } from "./components/gateways-empty-state";
import { GatewaysTable } from "./components/table/gateways-table";
import { useGatewaysViewOptions } from "./components/table/gateways-view-options";
import { MCP_GATEWAYS_ROUTE_ID, type McpGatewaysSearch } from "./search-params";
import { filterGatewaysBySearch, groupGatewaysByBinding } from "./utils";

export const MCPGatewayList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { connect } = useSearch({ from: MCP_GATEWAYS_ROUTE_ID });
  const navigate = useNavigate();
  const { organizationId, projectId } = useOrgProjectRouteContext();

  const handleCreateClick = useCallback(() => {
    navigate({
      to: "/orgs/$organizationId/projects/$projectId/mcp-gateways/new",
      params: { organizationId, projectId },
    });
  }, [navigate, organizationId, projectId]);

  const {
    columnVisibility,
    setColumnVisibility,
    component: ViewOptionsComponent,
  } = useGatewaysViewOptions();

  const { data: gateways = [], isFetching } = useSuspenseGetMcpGateways();

  // If `connect=<id>` is in the URL but the id doesn't match any gateway in
  // the current list (wrong id, deleted, different project), silently strip
  // the param so the connect sheet doesn't get stuck trying to open.
  //
  // Guarded on `isFetching` so we don't race against an in-flight refetch
  // triggered by an invalidation (e.g. the create page creating a new gateway
  // and immediately deep-linking to `?connect=<new-id>` before the list query
  // has caught up).
  useEffect(() => {
    if (!connect || isFetching) {
      return;
    }
    const matches = gateways.some((g) => g.id === connect);
    if (matches) {
      return;
    }
    navigate({
      to: ".",
      search: (prev: McpGatewaysSearch) => ({
        ...prev,
        connect: "",
      }),
      replace: true,
    });
  }, [connect, gateways, isFetching, navigate]);

  const grouped = useMemo(() => {
    const filtered = filterGatewaysBySearch(gateways, searchTerm);
    return groupGatewaysByBinding(filtered);
  }, [gateways, searchTerm]);

  const sections: ResourceSection[] = useMemo(() => {
    const list: ResourceSection[] = [];
    let isFirstSectionWithData = true;

    if (grouped.project.length > 0) {
      list.push({
        binding: "project",
        itemsCount: grouped.project.length,
        resourceType: "MCP gateways",
        defaultOpen: isFirstSectionWithData,
        children: (
          <GatewaysTable
            columnVisibility={columnVisibility}
            items={grouped.project}
            onColumnVisibilityChange={setColumnVisibility}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    if (grouped.tenant.length > 0) {
      list.push({
        binding: "tenant",
        itemsCount: grouped.tenant.length,
        resourceType: "MCP gateways",
        defaultOpen: isFirstSectionWithData,
        children: (
          <GatewaysTable
            columnVisibility={columnVisibility}
            items={grouped.tenant}
            onColumnVisibilityChange={setColumnVisibility}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    if (grouped.static.length > 0) {
      list.push({
        binding: "static",
        itemsCount: grouped.static.length,
        resourceType: "MCP gateways",
        defaultOpen: isFirstSectionWithData,
        children: (
          <GatewaysTable
            columnVisibility={columnVisibility}
            items={grouped.static}
            onColumnVisibilityChange={setColumnVisibility}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    return list;
  }, [grouped, columnVisibility, setColumnVisibility]);

  return (
    <ListLayout
      actions={
        <CanProject permission={PROJECT.mcpGateways.create}>
          <Button onClick={handleCreateClick} size="sm">
            <Plus className="mr-2 size-4" />
            Create MCP gateway
          </Button>
        </CanProject>
      }
      description="Choose which apps your agents can use, all from one place."
      filters={
        <>
          <div className="flex items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 w-full pl-9"
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search MCP gateways..."
                value={searchTerm}
              />
            </div>
            <div className="flex items-center gap-2">
              {ViewOptionsComponent}
            </div>
          </div>
          <ResourceViewSwitcher />
        </>
      }
      title="MCP gateways"
    >
      {sections.length > 0 ? (
        <ResourceContainer
          defaultActiveTab={`${sections[0].binding}-${sections[0].resourceType}`}
          sections={sections}
        />
      ) : (
        <GatewaysEmptyState
          isFiltered={gateways.length > 0}
          onCreate={handleCreateClick}
        />
      )}
    </ListLayout>
  );
};
